/**
 * Product Detail Page Theme
 * Styles for product detail page components
 */

const ProductDetail = {
  parts: [
    'productTabsInformationContainer',
    'productTabsInformationTab',
    'productTabsInformationTabActive',
    'productTabsInformationTabInactive',
    'productTabsInformationTabText',
    'productName',
    'productLongDescription',
    'productViewWrapper',
    'specificationsContainer',
    'specificationsInnerBox',
    'specificationsHeader',
    'specificationsTitle',
    'specificationsExpandAllButton',
    'specificationsExpandAllText',
    'specificationsExpandAllIconBox',
    'specificationsAccordion',
    'specificationsAccordionItem',
    'specificationsAccordionButton',
    'specificationsAccordionPanel',
    'specificationsSectionTitle',
    'specificationsIconBox'
  ],

  baseStyle: {
    productTabsInformationContainer: {
      alignItems: 'center',
      display: 'flex',
      gap: 8, // 32px gap between tabs
      justifyContent: 'flex-start',
      borderBottom: '2px solid',
      borderColor: 'brandColors.greyscale10',
    },

    productTabsInformationTab: {
      alignItems: 'center',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      gap: {base: 2.875, lg: 2.875}, // 23px gap between text and underline (11.5 * 2 = 23px)
      marginBottom: '-2px',
      paddingBottom: '23px',
      position: 'relative',
    },

    productTabsInformationTabActive: {
      _after: {
        bg: 'brandColors.primaryDarkLinen', // #8C683B
        bottom: 0,
        content: '""',
        height: '3px',
        left: 0,
        position: 'absolute',
        right: 0,
        transform: 'translateZ(0)', // GPU acceleration
        transition: 'background-color 0.2s ease', // Smooth color transition
        width: '100%',
        willChange: 'background-color', // Performance hint
      },
    },

    productTabsInformationTabInactive: {
      _after: {
        bg: 'transparent',
        bottom: 0,
        content: '""',
        height: '3px',
        left: 0,
        position: 'absolute',
        right: 0,
        transform: 'translateZ(0)', // GPU acceleration
        transition: 'background-color 0.2s ease', // Smooth color transition
        width: '100%',
        willChange: 'background-color', // Performance hint
      },
    },

    productTabsInformationTabText: {
      fontFamily: 'heading',
      fontSize: 'md', // 16px
      fontWeight: 600,
      lineHeight: 1.2,
      whiteSpace: 'nowrap',
    },

    productName: {
      color: 'brandColors.greyscale',
      fontFamily: 'heading',
      fontSize: {base: '2rem', lg: '2rem'}, // 32px for all breakpoints
      fontWeight: 500, // Medium
      lineHeight: 1.2,
    },

    productLongDescription: {
      color: 'brandColors.greyscale',
      fontFamily: 'body',
      fontSize: 'sm', // 14px for all breakpoints
      fontWeight: 400,
      lineHeight: 1.4,
      // Nested element styles (applied via sx prop in component)
      nestedStyles: {
        '& p': {
          marginBottom: 4,
        },
        '& ul, & ol': {
          marginLeft: 4,
          marginBottom: 4,
        },
        '& li': {
          marginBottom: 2,
        },
        '& h3, & h4': {
          fontWeight: 'bold',
          marginTop: 4,
          marginBottom: 2,
        },
      },
    },

    productViewWrapper: {
      maxWidth: '90rem', // 1440px
      width: '100%',
      marginX: 'auto',
      paddingX: {base: 0, lg: 20}, // 0px on mobile, 80px on desktop
    },

    productViewWrapperInner: {
      paddingX: {base: 5, lg: 0}, // 0px on mobile, 80px on desktop
    },

    specificationsContainer: {
      bg: 'brandColors.primaryBG',
      marginX: 'auto',
      paddingBottom: {base: 6, lg: 30},
      paddingTop: {base: 6, lg: 20},
      paddingX: {base: 5, lg: 20},
      width: '100%',
    },

    specificationsInnerBox: {
      flex: {base: 1, lg: 5},
    },

    specificationsHeader: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: {base: 6, lg: 16},
    },

    specificationsTitle: {
      color: 'brandColors.greyscale',
      fontFamily: 'heading',
      fontSize: {base: '1.125rem', lg: '2.5rem'},
      fontWeight: 600,
      lineHeight: 1.2,
    },

    specificationsExpandAllButton: {
      color: 'brandColors.primaryDarkLinen',
      height: {base: '8.5', lg: 12},
      paddingLeft: 0,
      paddingRight: 0,
      paddingX: 0,
      px: {
        base: 0,
        lg: 0,
      },
      _active: {
        bg: 'transparent',
      },
      _hover: {
        bg: 'transparent',
      },
    },

    specificationsExpandAllText: {
      color: 'brandColors.primaryDarkLinen',
      fontFamily: 'body',
      fontSize: {base: '0.75rem', lg: '1.125rem'},
      fontWeight: 500,
      lineHeight: 1.2,
    },

    specificationsExpandAllIconBox: {
      alignItems: 'center',
      color: 'brandColors.primaryDarkLinen',
      display: 'inline-flex',
      height: {base: 3, lg: 5},
      justifyContent: 'center',
      width: {base: 3, lg: 5},
    },

    specificationsAccordion: {
      borderColor: 'transparent',
    },

    specificationsAccordionItem: {
      borderBottomWidth: '0',
      borderTopColor: 'brandColors.greyscale10',
      borderTopWidth: '1px',
    },

    specificationsAccordionButton: {
      height: {base: 'auto', lg: 'auto'},
      paddingLeft: 0,
      paddingRight: 0,
      paddingTop: 6,
      paddingBottom: 6,
      _hover: {
        bg: 'transparent',
      },
    },

    specificationsAccordionPanel: {
      fontSize: 'xs', // 12px for all breakpoints
      marginBottom: 6,
      marginTop: 0,
      paddingLeft: 0,
      paddingRight: 0,
    },

    specificationsSectionTitle: {
      color: 'brandColors.greyscale',
      flex: '1',
      fontSize: 'sm',
      fontWeight: 600,
      textAlign: 'left',
    },

    specificationsIconBox: {
      alignItems: 'center',
      color: 'brandColors.primaryDarkLinen',
      display: 'flex',
      height: 4,
      justifyContent: 'center',
      width: 4,
    },
  },
};

export default ProductDetail;
